import { Link } from "react-router-dom";
import {
  ShoppingBagIcon,
  InformationCircleIcon,
  XCircleIcon,
  CreditCardIcon,
} from "@heroicons/react/24/solid";
import useOrderReceivedPage from "@/viewModels/order-received/useOrderReceivedPage";
import { PaymentMethodId } from "@/constants/enums";
import OrderDetails, { FieldLabel, FieldValue } from "@/components/features/orders/OrderDetails";
import OfficialLINE from "@/components/ui/OfficialLINE";
import SEOMeta from "@/components/ui/meta/SEOMeta";
import { OrderStatus } from "@/types/order";

// #region Types
type PaymentRelatedOrderStatus =
  | OrderStatus.OnHold
  | OrderStatus.Pending
  | OrderStatus.Processing
  | OrderStatus.Failed;

type OrderStatusContent = {
  icon: React.ReactElement;
  title: string;
  subtitle: string;
  desc: string | React.ReactNode;
  showReminder: boolean;
};
// #endregion

// #region Helpers
const isPaymentRelatedOrderStatus = (status: OrderStatus): status is PaymentRelatedOrderStatus => {
  return (
    status === OrderStatus.OnHold ||
    status === OrderStatus.Pending ||
    status === OrderStatus.Processing ||
    status === OrderStatus.Failed
  );
};
// #endregion

const OrderReceivedPage = () => {
  const { ecpayPaymentResult, order, isLoading } = useOrderReceivedPage();

  if (!order || isLoading) return <OrderReceivedPageSkeleton />;

  if (order.status === OrderStatus.Draft)
    return <div>訂單仍在草稿狀態，請返回結帳頁完成必要資訊重新操作一次</div>;

  if (!isPaymentRelatedOrderStatus(order.status)) {
    return (
      <div className="brand-container max-w-screen-md space-y-2 py-4 text-center lg:py-8">
        <p className="brand-text-sm mb-4 text-neutral">
          此訂單狀態已更新，付款結果請前往「查看訂單」頁面查詢。
        </p>
        <Link to="/order" className="brand-link-with-underline">
          查看訂單
        </Link>
      </div>
    );
  }

  const { payment_method } = order;

  const getProcessingOrderTitle = () => {
    switch (payment_method.method_id) {
      case PaymentMethodId.COD:
        return "訂單已成立";
      case PaymentMethodId.ECPAY_CREDIT:
        return "已完成付款";
      default:
        return "處理中";
    }
  };

  const orderStatusContent: Record<PaymentRelatedOrderStatus, OrderStatusContent> = {
    [OrderStatus.OnHold]: {
      icon: <ShoppingBagIcon className="box-content size-12 p-2" />,
      title: "等待付款中",
      subtitle: "謝謝您的購買",
      desc: "訂單保留中，請查看以下銀行轉帳資訊，進行轉帳。",
      showReminder: true,
    },
    [OrderStatus.Pending]: {
      icon: <CreditCardIcon className="box-content size-12 p-2" />,
      title: "信用卡付款失敗",
      subtitle: `訊息代碼：${ecpayPaymentResult.rtnCode ?? ""}`,
      desc: `${ecpayPaymentResult.rtnMsg ?? ""} (並請至您的信用卡銀行確認交易紀錄)`,
      showReminder: false,
    },
    [OrderStatus.Processing]: {
      icon: <ShoppingBagIcon className="box-content size-12 p-2" />,
      title: getProcessingOrderTitle(),
      subtitle: "謝謝您的購買",
      desc: processingDesc,
      showReminder: true,
    },
    [OrderStatus.Failed]: {
      icon: <XCircleIcon className="box-content size-12 p-2" />,
      title: "付款失敗，請重新結帳",
      subtitle: "",
      desc: "請至您的信用卡銀行確認交易紀錄",
      showReminder: false,
    },
  };

  const statusContent = orderStatusContent[order.status];

  return (
    <main className="brand-main">
      <div className="brand-container max-w-screen-md space-y-2 py-4 lg:py-8">
        {/* 謝謝您的購買 & 綠界信用卡付款失敗 */}
        <section className="brand-card brand-order-details-card items-center text-center">
          {statusContent.icon}
          <h1 className="text-2xl font-bold">{statusContent.title}</h1>
          <h2 className="text-lg font-bold">{statusContent.subtitle}</h2>
          <p className="brand-text-sm text-neutral">{statusContent.desc}</p>
        </section>

        {/* TODO 銀行轉帳 */}
        {payment_method.method_id === PaymentMethodId.BACS && (
          <section className="brand-card brand-order-details-card">
            <h2 className="brand-order-details-card-title">銀行轉帳資訊</h2>
            <div>
              <FieldLabel>銀行(代碼)</FieldLabel>
              <FieldValue classes="text-base">{payment_method.bank_detail?.bank_name}</FieldValue>
            </div>
            <div>
              <FieldLabel>帳號</FieldLabel>
              <FieldValue classes="text-base">
                {payment_method.bank_detail?.account_number}
              </FieldValue>
            </div>
            <div className="alert alert-warning flex items-stretch gap-2 rounded-none text-left">
              <InformationCircleIcon className="box-content size-5" />
              <p className="text-sm">
                轉帳後，請透過
                <OfficialLINE />
                ， 聯繫並告知轉帳後五碼，經確認收到訂單款項後將安排出貨。
                <br />
                <br />
                請於一天內進行轉帳，逾期系統將自動取消訂單。
              </p>
            </div>
          </section>
        )}

        {/* 需知 */}
        {statusContent.showReminder && (
          <section className="brand-card brand-order-details-card items-center">
            <h2 className="brand-order-details-card-title">訂購需知提醒：</h2>
            <ul className="brand-text-sm text-secondar flex list-disc flex-col gap-1 pl-4">
              <li>
                <b className="text-accent">現貨商品</b>：下單後，約 2 個工作天內寄出。
              </li>
              <li>
                <b className="text-accent">非現貨商品</b>：週六進行烘焙，烘焙完成後約 2~3
                個工作天寄出。
              </li>
              <li className="text-neutral">付款選擇「銀行轉帳」者，需先完成轉帳，才會安排出貨。</li>
            </ul>
          </section>
        )}

        {/* 商品、訂單資訊、寄送、備註 */}
        <OrderDetails order={order} />

        <section className="flex justify-center py-8">
          <Link to="/shop">
            <button className="brand-btn-base btn btn-secondary m-auto w-full">繼續逛逛</button>
          </Link>
        </section>
      </div>

      {/* Meta Tag */}
      <SEOMeta pageTitle="收到訂單" />
    </main>
  );
};

const processingDesc = (
  <>
    目前正在處理中。請耐心等候，
    <br />
    我們將很快寄送確認通知信給您，
    <br />
    請留意您填寫的 Email 信箱。
  </>
);

const OrderReceivedPageSkeleton = () => (
  <div className="brand-container flex max-w-screen-md flex-col justify-center gap-4 mt-8">
    <div className="flex w-full animate-pulse justify-center rounded-md bg-base-300">
      <div className="flex w-full max-w-md flex-col items-center justify-center gap-4 p-8 *:rounded-md *:bg-base-300">
        <div className="size-16 rounded-3xl bg-base-300" />
        <div className="h-4 w-2/3" />
        <div className="h-4 w-2/3" />
        <div className="h-4 w-1/2" />
      </div>
    </div>
    <div className="flex w-full animate-pulse justify-center rounded-md bg-base-300">
      <div className="flex w-full max-w-md flex-col items-center justify-center gap-4 p-8 *:rounded-md *:bg-base-300">
        <div className="h-4 w-1/2" />
        <div className="h-4 w-full" />
        <div className="h-4 w-2/3" />
      </div>
    </div>
    <div className="flex w-full animate-pulse rounded-md bg-base-300">
      <div className="flex w-full max-w-md flex-col gap-8 p-8">
        <div className="h-6 w-16 rounded-md bg-base-300" />
        {Array.from({ length: 4 }).map(() => (
          <div className="space-y-2 *:rounded-md *:bg-base-300">
            <div className="h-4 w-16" />
            <div className="h-4 w-32" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default OrderReceivedPage;
